// ── Carbon-style icon set for rule cards ──────────────────────────────────────
// 24×24 viewBox, currentColor fills — icons inherit the parent text color so
// they can be tinted via `text-*` utilities on the wrapper.

type IconProps = { className?: string };

const base = 'w-full h-full';

function Svg({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className ?? base}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function ActivityIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M9 21.75a.75.75 0 0 1-.69-.465L4.747 12.75H1.5v-1.5h3.75a.75.75 0 0 1 .69.465L9 18.96l6.045-16.222a.75.75 0 0 1 .705-.488.75.75 0 0 1 .698.51l2.842 8.49h3.21v1.5h-3.75a.75.75 0 0 1-.712-.51L15.75 5.25 9.705 21.263A.75.75 0 0 1 9 21.75" />
    </Svg>
  );
}

export function AnalyticsIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M3 1.5H1.5V21A1.5 1.5 0 0 0 3 22.5h19.5V21H3z" />
      <path d="M22.5 6.75h-5.25v1.5h2.692l-5.692 5.693-3.217-3.226a.75.75 0 0 0-1.065 0L4.5 16.192l1.058 1.058 4.942-4.943 3.217 3.226a.75.75 0 0 0 1.066 0L21 9.308V12h1.5z" />
    </Svg>
  );
}

export function CalendarIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M19.5 3h-3V1.5H15V3H9V1.5H7.5V3h-3C3.675 3 3 3.675 3 4.5v15c0 .825.675 1.5 1.5 1.5h15c.825 0 1.5-.675 1.5-1.5v-15c0-.825-.675-1.5-1.5-1.5m0 16.5h-15V9h15zm0-12h-15v-3h3V6H9V4.5h6V6h1.5V4.5h3z" />
    </Svg>
  );
}

export function CenterCircleIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M22.5 11.25h-1.538a9.005 9.005 0 0 0-8.212-8.212V1.5h-1.5v1.538a9.005 9.005 0 0 0-8.212 8.212H1.5v1.5h1.538a9.005 9.005 0 0 0 8.212 8.212V22.5h1.5v-1.538a9.005 9.005 0 0 0 8.212-8.212H22.5zm-9.75 8.212V16.5h-1.5v2.962a7.51 7.51 0 0 1-6.712-6.712H7.5v-1.5H4.538a7.51 7.51 0 0 1 6.712-6.712V7.5h1.5V4.538a7.51 7.51 0 0 1 6.712 6.712H16.5v1.5h2.962a7.51 7.51 0 0 1-6.712 6.712" />
    </Svg>
  );
}

export function CertificateCheckIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M9 12H4.5v1.5H9zM12 9H4.5v1.5H12zM12 6H4.5v1.5H12z" />
      <path d="M10.5 19.5H3v-15h18V12h1.5V4.5A1.5 1.5 0 0 0 21 3H3a1.5 1.5 0 0 0-1.5 1.5v15A1.5 1.5 0 0 0 3 21h7.5z" />
      <path d="m16.5 19.193-1.943-1.943-1.057 1.058 3 3 6-6-1.058-1.058z" />
    </Svg>
  );
}

export function ChartColumnIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M20.25 21V4.5h-6V21h-3V10.5h-6V21H3V1.5H1.5V21A1.5 1.5 0 0 0 3 22.5h19.5V21zm-10.5 0h-3v-9h3zm9 0h-3V6h3z" />
    </Svg>
  );
}

export function ChartHighLowIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M11.25 6H9V4.5H7.5V15H5.25v1.5H7.5V18H9V7.5h2.25zM20.25 7.5H18v-3h-1.5v9h-2.25V15h2.25v3H18V9h2.25z" />
      <path d="M22.5 22.5H3A1.5 1.5 0 0 1 1.5 21V1.5H3V21h19.5z" />
    </Svg>
  );
}

export function ChartPieIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18m7.5 8.25h-6.75V4.538a7.5 7.5 0 0 1 6.75 6.712m-7.935 8.25a7.5 7.5 0 0 1-.315-14.962v6.75a1.5 1.5 0 0 0 1.5 1.5h6.75a7.5 7.5 0 0 1-7.935 6.712" />
    </Svg>
  );
}

export function ErrorIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M1.5 12a10.5 10.5 0 1 0 21 0 10.5 10.5 0 0 0-21 0m17.363 5.813L6.188 5.137a9 9 0 0 1 12.675 12.675M6.18 18.87A9 9 0 0 1 5.13 6.203L17.798 18.87a9 9 0 0 1-11.618 0" />
    </Svg>
  );
}

export function GatewayMailIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M14.908 9.091a.31.31 0 0 0-.327-.072l-6.875 2.5a.313.313 0 0 0-.01.584l3.001 1.2 1.2 3a.31.31 0 0 0 .29.197h.006a.31.31 0 0 0 .288-.206l2.5-6.875a.31.31 0 0 0-.073-.328" />
      <path d="M22.81 13.06a1.5 1.5 0 0 0 0-2.12L18.47 6.6l2.176-2.148a1.501 1.501 0 1 0-1.081-1.04L17.41 5.539l-4.35-4.35a1.5 1.5 0 0 0-2.12 0L6.6 5.53 4.452 3.354a1.501 1.501 0 1 0-1.04 1.081L5.538 6.59l-4.35 4.35a1.5 1.5 0 0 0 0 2.12l4.35 4.35-2.127 2.155a1.499 1.499 0 1 0 1.04 1.081L6.6 18.471l4.34 4.34a1.5 1.5 0 0 0 2.12 0l4.35-4.35 2.155 2.127a1.498 1.498 0 1 0 1.081-1.04L18.471 17.4zM12 21.75 2.25 12 12 2.25 21.75 12z" />
    </Svg>
  );
}

export function IBMCloudDirectLinkIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M20.25 21.75H9.75c-.825 0-1.5-.675-1.5-1.5v-3h1.5v3h10.5V9.75h-3v-1.5h3c.825 0 1.5.675 1.5 1.5v10.5c0 .825-.675 1.5-1.5 1.5" />
      <path d="M14.25 15.75h-4.5c-.825 0-1.5-.675-1.5-1.5v-4.5c0-.825.675-1.5 1.5-1.5h4.5c.825 0 1.5.675 1.5 1.5v4.5c0 .825-.675 1.5-1.5 1.5m-4.5-6v4.5h4.5v-4.5z" />
      <path d="M3.75 2.25h10.5c.825 0 1.5.675 1.5 1.5v3h-1.5v-3H3.75v10.5h3v1.5h-3c-.825 0-1.5-.675-1.5-1.5V3.75c0-.825.675-1.5 1.5-1.5" />
    </Svg>
  );
}

export function LightningIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M8.708 22.44a.75.75 0 0 1-.45-.803l1.365-8.887H6.001a.75.75 0 0 1-.75-.922l2.25-9.75a.75.75 0 0 1 .75-.578h7.5a.75.75 0 0 1 .735.915L15.188 8.25h3.563a.75.75 0 0 1 .675.42.75.75 0 0 1-.083.75l-9.75 12.75a.75.75 0 0 1-.592.33.8.8 0 0 1-.293-.06m4.605-12.69 1.5-6.75H8.851l-1.905 8.25h4.432l-1.192 7.71 7.065-9.21z" />
    </Svg>
  );
}

export function LoadBalancerVpcIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M6 22.5H1.5V18H6zM3 21h1.5v-1.5H3zM14.25 22.5h-4.5V18h4.5zm-3-1.5h1.5v-1.5h-1.5zM22.5 22.5H18V18h4.5zm-3-1.5H21v-1.5h-1.5zM12 7.5a3 3 0 1 1 0-6 3 3 0 0 1 0 6M12 3a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M19.5 12h-3.75a3.003 3.003 0 0 1-3-3h-1.5a3.003 3.003 0 0 1-3 3H4.5A1.5 1.5 0 0 0 3 13.5v3h1.5v-3h3.75a4.48 4.48 0 0 0 3-1.155V16.5h1.5v-4.155a4.48 4.48 0 0 0 3 1.155h3.75v3H21v-3a1.5 1.5 0 0 0-1.5-1.5" />
    </Svg>
  );
}

export function LockedIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M18 10.5h-1.5V6a4.5 4.5 0 1 0-9 0v4.5H6A1.5 1.5 0 0 0 4.5 12v9A1.5 1.5 0 0 0 6 22.5h12a1.5 1.5 0 0 0 1.5-1.5v-9a1.5 1.5 0 0 0-1.5-1.5M9 6a3 3 0 1 1 6 0v4.5H9zm9 15H6v-9h12z" />
    </Svg>
  );
}

export function MoonIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M10.127 4.06a11.306 11.306 0 0 0 8.695 13.646 8.34 8.34 0 0 1-5.981 2.542c-.104 0-.209.004-.313 0a8.32 8.32 0 0 1-2.4-16.188m1.108-1.81a1 1 0 0 0-.13.012 9.822 9.822 0 0 0 1.368 19.486c.123.005.246 0 .368 0a9.8 9.8 0 0 0 8.026-4.167.757.757 0 0 0-.587-1.173 9.81 9.81 0 0 1-8.363-13.123.76.76 0 0 0-.682-1.035" />
    </Svg>
  );
}

export function PurchaseIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M21 4.5H3A1.5 1.5 0 0 0 1.5 6v12A1.5 1.5 0 0 0 3 19.5h18a1.5 1.5 0 0 0 1.5-1.5V6A1.5 1.5 0 0 0 21 4.5M21 6v2.25H3V6zM3 18V9.75h18V18z" />
      <path d="M12 15H4.5v1.5H12z" />
    </Svg>
  );
}

export function ReflectHorizontalIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="m14.54 11.408 6.75-5.25a.75.75 0 0 1 1.21.592v10.5a.75.75 0 0 1-1.21.593l-6.75-5.25a.75.75 0 0 1 0-1.185M11.25 22.5h1.5v-21h-1.5zM9.75 12a.75.75 0 0 1-.29.592l-6.75 5.25a.75.75 0 0 1-1.21-.592V6.75a.75.75 0 0 1 1.21-.593l6.75 5.25a.75.75 0 0 1 .29.593M3 15.717 7.778 12 3 8.283z" />
    </Svg>
  );
}

export function TagIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M13.89 22.5a2.25 2.25 0 0 1-1.59-.66L2.16 11.708a2.25 2.25 0 0 1-.66-1.59V3.75A2.25 2.25 0 0 1 3.75 1.5h6.368a2.25 2.25 0 0 1 1.59.66L21.84 12.293a2.25 2.25 0 0 1 0 3.187l-6.36 6.36a2.25 2.25 0 0 1-1.59.66M3.75 3a.75.75 0 0 0-.75.75v6.368a.75.75 0 0 0 .225.532l10.133 10.132a.75.75 0 0 0 1.057 0l6.368-6.367a.75.75 0 0 0 0-1.057L10.65 3.225A.75.75 0 0 0 10.117 3z" />
      <path d="M7.5 10.5a3 3 0 1 1 0-6 3 3 0 0 1 0 6m0-4.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3" />
    </Svg>
  );
}

export function ToolBoxIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M20.25 6.75H18V4.5A1.5 1.5 0 0 0 16.5 3h-9A1.5 1.5 0 0 0 6 4.5v2.25H3.75A2.25 2.25 0 0 0 1.5 9v10.5A1.5 1.5 0 0 0 3 21h18a1.5 1.5 0 0 0 1.5-1.5V9a2.25 2.25 0 0 0-2.25-2.25M7.5 4.5h9v2.25h-9zm13.5 15H3v-6.75h6v3.75h6v-3.75h6zm-10.5-6.75h3V15h-3zM3 11.25V9a.75.75 0 0 1 .75-.75h16.5A.75.75 0 0 1 21 9v2.25z" />
    </Svg>
  );
}

export function UserAdminIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M9 3C9.74168 3 10.4667 3.21993 11.0834 3.63199C11.7001 4.04404 12.1807 4.62971 12.4645 5.31494C12.7484 6.00016 12.8226 6.75416 12.6779 7.48159C12.5333 8.20902 12.1761 8.8772 11.6517 9.40165C11.1272 9.9261 10.459 10.2833 9.73159 10.4279C9.00416 10.5726 8.25016 10.4984 7.56494 10.2145C6.87971 9.93072 6.29404 9.45007 5.88199 8.83339C5.46993 8.2167 5.25 7.49168 5.25 6.75C5.25 5.75544 5.64509 4.80161 6.34835 4.09835C7.05161 3.39509 8.00544 3 9 3ZM9 1.5C7.96165 1.5 6.94661 1.80791 6.08326 2.38478C5.2199 2.96166 4.54699 3.7816 4.14963 4.74091C3.75227 5.70022 3.6483 6.75582 3.85088 7.77422C4.05345 8.79262 4.55346 9.72808 5.28769 10.4623C6.02191 11.1965 6.95738 11.6966 7.97578 11.8991C8.99418 12.1017 10.0498 11.9977 11.0091 11.6004C11.9684 11.203 12.7883 10.5301 13.3652 9.66674C13.9421 8.80339 14.25 7.78835 14.25 6.75C14.25 5.35761 13.6969 4.02226 12.7123 3.03769C11.7277 2.05312 10.3924 1.5 9 1.5Z" />
      <path d="M16.5 22.5H15V18.75C15 17.7554 14.6049 16.8016 13.9017 16.0983C13.1984 15.3951 12.2446 15 11.25 15H6.75C5.75544 15 4.80161 15.3951 4.09835 16.0983C3.39509 16.8016 3 17.7554 3 18.75V22.5H1.5V18.75C1.5 17.3576 2.05312 16.0223 3.03769 15.0377C4.02226 14.0531 5.35761 13.5 6.75 13.5H11.25C12.6424 13.5 13.9777 14.0531 14.9623 15.0377C15.9469 16.0223 16.5 17.3576 16.5 18.75V22.5Z" />
      <path d="M18.75 12.135L16.8075 10.1925L15.75 11.25L18.75 14.25L24 9L22.9425 7.9425L18.75 12.135Z" />
    </Svg>
  );
}

export function UserMultipleIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M22.5 22.5H21v-3.75A3.754 3.754 0 0 0 17.25 15v-1.5a5.256 5.256 0 0 1 5.25 5.25zM16.5 22.5H15v-3.75A3.755 3.755 0 0 0 11.25 15h-4.5A3.755 3.755 0 0 0 3 18.75v3.75H1.5v-3.75a5.256 5.256 0 0 1 5.25-5.25h4.5a5.256 5.256 0 0 1 5.25 5.25zM15 1.5V3a3.75 3.75 0 1 1 0 7.5V12a5.25 5.25 0 1 0 0-10.5M9 3a3.75 3.75 0 1 1 0 7.5A3.75 3.75 0 0 1 9 3m0-1.5A5.25 5.25 0 1 0 9 12 5.25 5.25 0 0 0 9 1.5" />
    </Svg>
  );
}

export function WarningAltIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M12 17.25a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25M12.75 9h-1.5v6.75h1.5z" />
      <path d="M21.75 22.5H2.25a.75.75 0 0 1-.665-1.096l9.75-18.75a.75.75 0 0 1 1.33 0l9.75 18.75a.75.75 0 0 1-.665 1.096M3.488 21h17.024l.002-.003L12.002 4.63h-.004L3.488 20.997z" />
    </Svg>
  );
}

export function WarningHexIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M12 15.75A1.125 1.125 0 1 0 12 18a1.125 1.125 0 0 0 0-2.25M12.75 6h-1.5v7.5h1.5z" />
      <path d="M17.25 21.75H6.75a.75.75 0 0 1-.648-.372l-5.25-9a.75.75 0 0 1 0-.756l5.25-9a.75.75 0 0 1 .648-.372h10.5a.75.75 0 0 1 .648.372l5.25 9a.75.75 0 0 1 0 .756l-5.25 9a.75.75 0 0 1-.648.372m-10.07-1.5h9.64L21.631 12l-4.813-8.25H7.181L2.368 12z" />
    </Svg>
  );
}

// ── Icon dispatcher ──────────────────────────────────────────────────────────

export const ICON_MAP: Record<string, (p: IconProps) => JSX.Element> = {
  activity: ActivityIcon,
  analytics: AnalyticsIcon,
  calendar: CalendarIcon,
  'center-circle': CenterCircleIcon,
  'certificate-check': CertificateCheckIcon,
  'chart-column': ChartColumnIcon,
  'chart-high-low': ChartHighLowIcon,
  'chart-pie': ChartPieIcon,
  error: ErrorIcon,
  'gateway-mail': GatewayMailIcon,
  'ibm-cloud-direct-link': IBMCloudDirectLinkIcon,
  lightning: LightningIcon,
  'load-balancer': LoadBalancerVpcIcon,
  'load-balancer-vpc': LoadBalancerVpcIcon, // alias for existing seed data
  locked: LockedIcon,
  moon: MoonIcon,
  purchase: PurchaseIcon,
  'reflect-horizontal': ReflectHorizontalIcon,
  tag: TagIcon,
  'tool-box': ToolBoxIcon,
  'user-admin': UserAdminIcon,
  'user-multiple': UserMultipleIcon,
  'warning-alt': WarningAltIcon,
  'warning-hex': WarningHexIcon,
};

export const ICON_TYPES = Object.keys(ICON_MAP).filter(
  // Hide the alias from the Studio dropdown
  (k) => k !== 'load-balancer-vpc',
);

export function RuleIcon({
  type,
  className,
}: {
  type?: string;
  className?: string;
}) {
  if (!type) return null;
  const key = type.toLowerCase().replace(/[_\s]+/g, '-');
  const Component = ICON_MAP[key];
  if (!Component) return null;
  return <Component className={className} />;
}
